import { useEffect, useMemo, useRef, useState } from "react";
import { apiV2 } from "../../../api/axiosConfig";

// --- Types ---

// Raw point as returned by the backend (no combining)
type RawPoint = {
    coordinates: [number, number]; // [lng, lat]
    type: string;
    startTime: string;
    endTime: string;
    bearing: number | null;
    pointCount: number;
    speed: number[] | null;
    battery: number[] | null;
    name: string;
    clientName: string;
};

type LocationFeedResponse = {
    points: RawPoint[];
    nextCursor: string;
};

// Combined/clustered point (same shape — combining merges fields in-place)
export type TrackingPoint = RawPoint;

export type TrackingPopupState = {
    visible: boolean;
    position: { x: number; y: number };
    points: TrackingPoint[];
    latestEndTime: string; // endTime of the last point in the full dataset
    groupIndex: number;    // index into the current cluster groups array
    groupCount: number;    // total number of cluster groups
};

// --- Combining (ported from backend) ---

const COMBINE_METRES = 25;

function haversineMetres(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function combinePoints(pts: RawPoint[]): TrackingPoint[] {
    if (pts.length <= 1) return pts;
    const out: TrackingPoint[] = [{ ...pts[0], speed: pts[0].speed ? [...pts[0].speed] : null, battery: pts[0].battery ? [...pts[0].battery] : null }];
    for (let i = 1; i < pts.length; i++) {
        const prev = out[out.length - 1];
        const cur = pts[i];
        const dist = haversineMetres(prev.coordinates[1], prev.coordinates[0], cur.coordinates[1], cur.coordinates[0]);
        if (dist < COMBINE_METRES && cur.type === "location") {
            prev.endTime = cur.endTime;
            prev.pointCount++;
            if (cur.battery && cur.battery.length > 0) prev.battery = [...(prev.battery ?? []), ...cur.battery];
            if (cur.speed && cur.speed.length > 0) prev.speed = [...(prev.speed ?? []), ...cur.speed];
        } else {
            out.push({ ...cur, speed: cur.speed ? [...cur.speed] : null, battery: cur.battery ? [...cur.battery] : null });
        }
    }
    return out;
}

// --- Fetch ---

async function fetchFull(feedURL: string, days: number): Promise<{ points: TrackingPoint[]; cursor: string }> {
    const res = await apiV2.get<LocationFeedResponse>(feedURL, { params: { days } });
    const combined = combinePoints(res.data.points);
    console.log("[tracking] fetchFull", feedURL, "raw:", res.data.points.length, "combined:", combined.length, "types:", [...new Set(res.data.points.map(p => p.type))]);
    return { points: combined, cursor: res.data.nextCursor };
}

async function fetchIncremental(feedURL: string, since: string): Promise<{ points: RawPoint[]; cursor: string }> {
    const res = await apiV2.get<LocationFeedResponse>(feedURL, { params: { since } });
    return { points: res.data.points, cursor: res.data.nextCursor };
}

// --- Constants ---

const PURPLE         = "#8b71e8";
const PURPLE_LIGHT   = "#c4b5fd";
const HIT_RADIUS    = 14;  // px — hit test radius for click/hover
const CLUSTER_PX    = 20;  // px — points closer than this in screen space collapse into one dot (zoom-dependent naturally)
const ANIM_SPEED_PX_PER_MS = 0.52;
const ANIM_MIN_LOOP_MS = 2_500;
const ANIM_MAX_LOOP_MS = 8_000;
const LATEST_PULSE_LOOP_MS = 1_800;

// --- Overlay factory ---

type TrackingOverlayInstance = {
    setPoints(points: TrackingPoint[]): void;
    getHitsAtPoint(clientX: number, clientY: number): { points: TrackingPoint[]; nearbyCount: number; nearestIdx: number; latestPoint: TrackingPoint; groupIndex: number; groupCount: number } | null;
    getGroupAt(index: number): { points: TrackingPoint[]; lat: number; lng: number } | null;
    setHoveredIdx(idx: number | null): void;
    destroy(): void;
};

function createTrackingOverlay(map: google.maps.Map): TrackingOverlayInstance {
    const svgContainer = document.createElement("div");
    svgContainer.style.cssText = "position:absolute;top:0;left:0;pointer-events:none;overflow:visible;z-index:10";
    svgContainer.setAttribute("data-tracking-overlay", "1");

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.style.cssText = "position:absolute;top:0;left:0;overflow:visible;pointer-events:none";
    svg.setAttribute("width", "0");
    svg.setAttribute("height", "0");
    const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svg.appendChild(svgGroup);
    svgContainer.appendChild(svg);

    let points: TrackingPoint[] = [];
    let lastGroups: { anchorIdx: number; memberIdxs: number[]; px: { x: number; y: number } }[] = [];
    let animPhase = 0;
    let lastTs = 0;
    let lastVisSlice = "";
    let animFrameId = 0;
    let projection: google.maps.MapCanvasProjection | null = null;
    let added = false;
    let hoveredIdx: number | null = null;

    // Coords relative to map container div — stable, unaffected by overlay pane CSS transforms
    function toPixel(lat: number, lng: number): { x: number; y: number } {
        if (!projection) return { x: 0, y: 0 };
        const p = projection.fromLatLngToContainerPixel(new google.maps.LatLng(lat, lng));
        return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
    }

    // Consecutive clustering: merge runs of adjacent points (in chronological order) that stay
    // within clusterR pixels of the run's first point. A return visit to the same location later
    // in the path starts a new group because the chain was broken by intermediate distant points.
    function buildGroups(pts: { x: number; y: number }[], clusterR: number) {
        const groups: { anchorIdx: number; memberIdxs: number[]; px: { x: number; y: number } }[] = [];
        let i = 0;
        while (i < pts.length) {
            const members: number[] = [i];
            const anchor = pts[i];
            let j = i + 1;
            while (j < pts.length && Math.hypot(pts[j].x - anchor.x, pts[j].y - anchor.y) <= clusterR) {
                members.push(j);
                j++;
            }
            groups.push({ anchorIdx: i, memberIdxs: members, px: anchor });
            i = j;
        }
        return groups;
    }

    function renderSVG() {
        if (!added || points.length === 0 || !projection) {
            svgGroup.innerHTML = "";
            return;
        }

        const pts = points.map((p) => toPixel(p.coordinates[1], p.coordinates[0]));
        const groups = buildGroups(pts, CLUSTER_PX);
        lastGroups = groups;
        const lastPointIdx = points.length - 1;
        const parts: string[] = [];

        // Use cluster dot positions for the line and gradient
        const gPts = groups.map((g) => g.px);
        parts.push(`<defs>
            <filter id="tk-halo" x="-200%" y="-200%" width="500%" height="500%">
                <feGaussianBlur stdDeviation="6" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
        </defs>`);

        const poly = gPts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
        parts.push(`<polyline points="${poly}" fill="none" stroke="${PURPLE}" stroke-width="10" stroke-opacity="0.12" stroke-linecap="round" stroke-linejoin="round"/>`);
        parts.push(`<polyline points="${poly}" fill="none" stroke="${PURPLE}" stroke-width="2.5" stroke-opacity="0.6" stroke-linecap="round" stroke-linejoin="round"/>`);

        // Animated streaks — collapse off-screen runs into short fake exit/entry points
        if (gPts.length > 1) {
            const mapDiv = map.getDiv() as HTMLElement;
            const W = mapDiv.offsetWidth;
            const H = mapDiv.offsetHeight;
            const MARGIN = 80;
            // Stub length: enough to clearly exit the viewport
            const OFFSCREEN_STUB = Math.max(W, H);
            // A group is off-screen if its true pixel position (first member) is outside bounds
            const inBounds = (p: { x: number; y: number }) =>
                p.x >= -MARGIN && p.x <= W + MARGIN && p.y >= -MARGIN && p.y <= H + MARGIN;
            const groupInBounds = (gi: number) => inBounds(pts[groups[gi].memberIdxs[0]]);

            const project = (from: { x: number; y: number }, to: { x: number; y: number }, dist: number) => {
                const dx = to.x - from.x, dy = to.y - from.y;
                const d = Math.sqrt(dx*dx + dy*dy) || 1;
                return { x: from.x + dx/d * dist, y: from.y + dy/d * dist };
            };

            // Build compressed point list using gPts: off-screen runs replaced with exit+entry stubs
            const animPts: { x: number; y: number }[] = [];
            let i = 0;
            while (i < gPts.length) {
                if (groupInBounds(i)) {
                    animPts.push(gPts[i]);
                    i++;
                } else {
                    const runStart = i;
                    while (i < gPts.length && !groupInBounds(i)) i++;
                    const runEnd = i;

                    // Exit stub: project off-screen from last visible group in the direction we left
                    if (runStart > 0) {
                        animPts.push(project(gPts[runStart - 1], gPts[runStart], OFFSCREEN_STUB));
                    }
                    // Entry stub: project off-screen from first visible group after run, back the way we came
                    if (runEnd < gPts.length) {
                        animPts.push(project(gPts[runEnd], gPts[runEnd - 1], OFFSCREEN_STUB));
                    }
                }
            }

            console.log("[tracking] gPts", gPts.map((p, i) => `${i}:(${p.x.toFixed(0)},${p.y.toFixed(0)}) inBounds=${groupInBounds(i)}`));
            console.log("[tracking] animPts", animPts.map(p => `(${p.x.toFixed(0)},${p.y.toFixed(0)})`));
            // Only animate if there are at least 2 points and at least one is visible
            const hasVisible = animPts.some(inBounds);
            if (hasVisible && animPts.length > 1) {
                const visSlice = animPts.map(p => `${p.x.toFixed(0)},${p.y.toFixed(0)}`).join("|");
                if (visSlice !== lastVisSlice) { animPhase = 0; lastVisSlice = visSlice; }

                const animPoly = animPts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

                let totalLen = 0;
                for (let j = 1; j < animPts.length; j++) {
                    const dx = animPts[j].x - animPts[j-1].x;
                    const dy = animPts[j].y - animPts[j-1].y;
                    totalLen += Math.sqrt(dx*dx + dy*dy);
                }

                if (totalLen > 0) {
                    const streakLen = Math.min(120, totalLen * 0.25);
                    const gap = Math.max(totalLen + streakLen, streakLen * 5);
                    const unclampedLoopMs = gap / ANIM_SPEED_PX_PER_MS;
                    const loopMs = Math.min(ANIM_MAX_LOOP_MS, Math.max(ANIM_MIN_LOOP_MS, unclampedLoopMs));
                    const effectiveSpeed = gap / loopMs;
                    const sharedOffset = (animPhase * effectiveSpeed) % gap;

                    // Wide soft glow streak
                    const tail1 = Math.max(0, sharedOffset - streakLen);
                    const vis1 = Math.min(streakLen, sharedOffset, totalLen - tail1);
                    if (vis1 > 0) {
                        parts.push(`<polyline points="${animPoly}" fill="none" stroke="#e8e0ff" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="${vis1.toFixed(1)},${(gap * 2).toFixed(1)}" stroke-dashoffset="${(-tail1).toFixed(1)}" opacity="0.35"/>`);
                    }

                    // Bright narrow core streak — centred on the glow
                    const coreLen = Math.min(60, totalLen * 0.12);
                    const coreOffset = (sharedOffset - (streakLen - coreLen) / 2 + gap) % gap;
                    const tail2 = Math.max(0, coreOffset - coreLen);
                    const vis2 = Math.min(coreLen, coreOffset, totalLen - tail2);
                    if (vis2 > 0) {
                        parts.push(`<polyline points="${animPoly}" fill="none" stroke="#e8e0ff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="${vis2.toFixed(1)},${(gap * 2).toFixed(1)}" stroke-dashoffset="${(-tail2).toFixed(1)}" opacity="0.95"/>`);
                    }
                }
            }
        }


        // Dots — one per cluster group, fixed size regardless of cluster count
        const phase2π = (animPhase / LATEST_PULSE_LOOP_MS) * Math.PI * 2;
        for (let gi = 0; gi < groups.length; gi++) {
            const { memberIdxs, px } = groups[gi];
            const isHovered = hoveredIdx !== null && memberIdxs.includes(hoveredIdx);
            const isLatest = memberIdxs.includes(lastPointIdx);
            const isFirst = gi === 0 && memberIdxs.includes(0);

            if (isLatest) {
                const latestR = isHovered ? 13 : 10;
                const r1 = 11 + 6 * Math.sin(phase2π);
                const r2 = 19 + 6 * Math.sin(phase2π + Math.PI);
                const a1 = Math.max(0, 0.4 * (1 - 0.5 * Math.sin(phase2π))).toFixed(2);
                const a2 = Math.max(0, 0.2 * (1 - 0.5 * Math.sin(phase2π + Math.PI))).toFixed(2);
                parts.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="${r2.toFixed(1)}" fill="none" stroke="${PURPLE}" stroke-width="1.5" opacity="${a2}"/>`);
                parts.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="${r1.toFixed(1)}" fill="none" stroke="${PURPLE}" stroke-width="2" opacity="${a1}"/>`);
                parts.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="${latestR}" fill="${PURPLE}" stroke="${PURPLE_LIGHT}" stroke-width="2.5" filter="url(#tk-halo)"/>`);
                parts.push(`<text x="${px.x.toFixed(1)}" y="${(px.y + 4).toFixed(1)}" text-anchor="middle" font-size="11" font-family="Material Icons" fill="#fff">person</text>`);
            } else if (isFirst) {
                const r = isHovered ? 10 : 7;
                parts.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="${r}" fill="${PURPLE}" stroke="#e8e0ff" stroke-width="2"/>`);
            } else {
                const r = isHovered ? 7 : 4.5;
                parts.push(`<circle cx="${px.x.toFixed(1)}" cy="${px.y.toFixed(1)}" r="${r}" fill="${PURPLE}" stroke="#e8e0ff" stroke-width="1.5" opacity="${isHovered ? 1 : 0.85}"/>`);
            }
        }

        svgGroup.innerHTML = parts.join("\n");
    }

    const overlay = new (class extends google.maps.OverlayView {
        onAdd() {
            // Attach directly to map div — matches fromLatLngToContainerPixel coordinate space
            (map.getDiv() as HTMLElement).appendChild(svgContainer);
            added = true;
            const count = (map.getDiv() as HTMLElement).querySelectorAll("[data-tracking-overlay]").length;
            console.log("[tracking] overlay added, total containers:", count);
        }
        draw() {
            projection = this.getProjection();
            const mapDiv = map.getDiv() as HTMLElement;
            svg.setAttribute("width", String(mapDiv.offsetWidth));
            svg.setAttribute("height", String(mapDiv.offsetHeight));
            renderSVG();
        }
        onRemove() {
            added = false;
            cancelAnimationFrame(animFrameId);
            svgContainer.remove();
        }
    })();

    overlay.setMap(map);

    function startAnimation() {
        function tick(ts: number) {
            const dt = lastTs === 0 ? 16 : ts - lastTs;
            lastTs = ts;
            animPhase += dt; // pixels per ms — constant visual speed
            renderSVG();
            animFrameId = requestAnimationFrame(tick);
        }
        animFrameId = requestAnimationFrame(tick);
    }
    startAnimation();

    return {
        setPoints(newPoints: TrackingPoint[]) {
            points = newPoints;
            lastGroups = [];
            renderSVG();
            console.log("[tracking] setPoints:", newPoints.length, "points, types:", [...new Set(newPoints.map(p => p.type))]);
        },
        getHitsAtPoint(clientX: number, clientY: number) {
            if (points.length === 0 || !projection) return null;
            const mapRect = (map.getDiv() as HTMLElement).getBoundingClientRect();
            const cx = clientX - mapRect.left;
            const cy = clientY - mapRect.top;

            const containerPts = points.map((p) => toPixel(p.coordinates[1], p.coordinates[0]));

            // Find nearest raw point within HIT_RADIUS (fixed UX affordance)
            let bestIdx = -1;
            let bestDist = Infinity;
            for (let i = 0; i < containerPts.length; i++) {
                const dist = Math.hypot(containerPts[i].x - cx, containerPts[i].y - cy);
                if (dist <= HIT_RADIUS && dist < bestDist) { bestDist = dist; bestIdx = i; }
            }
            if (bestIdx === -1) return null;

            // Find which group the nearest point belongs to
            const groupIndex = lastGroups.findIndex((g) => g.memberIdxs.includes(bestIdx));
            const group = groupIndex >= 0 ? lastGroups[groupIndex] : null;
            const nearby = group ? group.memberIdxs.map((i) => points[i]) : [points[bestIdx]];
            return { points: nearby, nearbyCount: nearby.length, nearestIdx: bestIdx, latestPoint: points[points.length - 1], groupIndex: Math.max(0, groupIndex), groupCount: lastGroups.length };
        },
        getGroupAt(index: number) {
            const group = lastGroups[index];
            if (!group) return null;
            const anchor = points[group.anchorIdx];
            return {
                points: group.memberIdxs.map((i) => points[i]),
                lat: anchor.coordinates[1],
                lng: anchor.coordinates[0],
            };
        },
        setHoveredIdx(idx: number | null) {
            hoveredIdx = idx;
        },
        destroy() {
            cancelAnimationFrame(animFrameId);
            overlay.setMap(null);
            svgContainer.remove();
            console.log("[tracking] overlay destroyed, remaining containers:", (map.getDiv() as HTMLElement).querySelectorAll("[data-tracking-overlay]").length);
        },
    };
}

const POLL_INTERVAL_MS = 30_000;

// --- Hook ---

export function useTrackingLayer(
    map: google.maps.Map | null,
    userID: string | null,
    days = 1,
    deviceID: string | null = null,
) {
    const feedURL = useMemo(() =>
        deviceID
            ? `ops/device-location-feed/${deviceID}`
            : userID
                ? `ops/user-location-feed/${userID}`
                : null,
    [deviceID, userID]);

    const [points, setPoints] = useState<TrackingPoint[]>([]);
    const cursorRef = useRef<string>("");
    const overlayRef = useRef<TrackingOverlayInstance | null>(null);
    const firstLoadRef = useRef(true);
    const requestSeqRef = useRef(0);
    // Full fetch + incremental polling
    useEffect(() => {
        const requestSeq = ++requestSeqRef.current;
        if (!feedURL) {
            setPoints([]);
            cursorRef.current = "";
            return;
        }

        let cancelled = false;

        async function loadFull() {
            try {
                const { points: pts, cursor } = await fetchFull(feedURL!, days);
                if (cancelled || requestSeq !== requestSeqRef.current) return;
                setPoints(pts);
                cursorRef.current = pts.length > 0 ? pts[pts.length - 1].startTime : cursor;
                // Pan to latest point on first load
                if (firstLoadRef.current && pts.length > 0 && map) {
                    firstLoadRef.current = false;
                    const latest = pts[pts.length - 1];
                    map.setZoom(13);
                    map.panTo(new google.maps.LatLng(latest.coordinates[1], latest.coordinates[0]));
                }
            } catch {
                // ignore — will retry on next interval
            }
        }

        async function doPoll() {
            if (!cursorRef.current) return;
            try {
                const { points: newRaw, cursor } = await fetchIncremental(feedURL!, cursorRef.current);
                if (cancelled || requestSeq !== requestSeqRef.current || newRaw.length === 0) return;

                setPoints((prev) => {
                    // Drop the last combined point (it was the boundary seed) and re-combine it with new raw
                    const seed = prev.length > 0 ? prev[prev.length - 1] : null;
                    const rawSeed: RawPoint[] = seed ? [seed] : [];
                    const merged = combinePoints([...rawSeed, ...newRaw]);
                    // Replace the tail: keep all but last prev point, then append merged
                    const base = seed ? prev.slice(0, -1) : prev;
                    return [...base, ...merged];
                });
                cursorRef.current = cursor || cursorRef.current;
            } catch {
                // ignore
            }
        }
        setPoints([]);
        cursorRef.current = "";
        overlayRef.current?.setPoints([]);
        loadFull();
        const timerId = setInterval(doPoll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timerId);
        };
    }, [feedURL, days]);

    // Reset firstLoad flag when feed changes so we pan on next fetch
    useEffect(() => {
        firstLoadRef.current = true;
    }, [feedURL]);

    // Create/destroy overlay when map or feed changes
    useEffect(() => {
        if (!map || !feedURL) {
            overlayRef.current?.destroy();
            overlayRef.current = null;
            return;
        }
        overlayRef.current = createTrackingOverlay(map);
        return () => {
            overlayRef.current?.destroy();
            overlayRef.current = null;
        };
    }, [map, feedURL]);

    // Push points to overlay whenever they change
    useEffect(() => {
        overlayRef.current?.setPoints(points);
    }, [points]);

    const getHitsAtPoint = (clientX: number, clientY: number) =>
        overlayRef.current?.getHitsAtPoint(clientX, clientY) ?? null;

    const getGroupAt = (index: number) =>
        overlayRef.current?.getGroupAt(index) ?? null;

    const setHoveredIdx = (idx: number | null) =>
        overlayRef.current?.setHoveredIdx(idx);

    return { getHitsAtPoint, getGroupAt, setHoveredIdx };
}
